'use client';

import { ELocalStorageKeys, ESessionStorageKeys, EStorageType } from '@web-store-platforms/types';

interface IBaseWebStorageServiceProps {
  onError?: (key: string, error: unknown) => void;
}

/**
 * Base class for browser storage (localStorage / sessionStorage) with
 * SSR safety, JSON serialization, and session-fallback logic.
 * Apps extend this to provide their own `clear()` strategy and optional
 * error-reporting integration (e.g. Coralogix, console).
 *
 * @example
 * class AppStorageService extends BaseWebStorageService {
 *   clear(): void { … }
 * }
 * export const webStorageUtil = new AppStorageService();
 */
export abstract class BaseWebStorageService {
  protected sessionStorageKeys: string[] = Object.values(ESessionStorageKeys);
  protected localStorageKeys: string[] = Object.values(ELocalStorageKeys);
  protected isClient = typeof window !== 'undefined';
  private onError?: (key: string, error: unknown) => void;

  constructor(props?: IBaseWebStorageServiceProps) {
    this.onError = props?.onError;
  }

  getStorage = (key: ELocalStorageKeys | ESessionStorageKeys): EStorageType => {
    const isSession = this.sessionStorageKeys.includes(key);
    const isLocal = this.localStorageKeys.includes(key);
    if (!isSession && !isLocal) {
      throw new Error("storage key isn't defined");
    }
    return isSession ? EStorageType.SESSION_STORAGE : EStorageType.LOCAL_STORAGE;
  };

  /**
   * Writes a value, or reports that it could not be written.
   *
   * Reaching `window.localStorage` can throw, not just reading a key out of it:
   * a document with storage blocked — a third-party iframe whose cookies are
   * refused, or a WebView built without DOM storage — raises SecurityError on the
   * property access itself. Apps write from mount effects on the boot path, so an
   * unguarded write there does not lose a value, it throws inside the render and
   * leaves the player on a dead page. Not persisting is always recoverable; not
   * rendering is not, so this fails the way `get` already does.
   *
   * The key check stays outside: a key belonging to neither storage is a mistake
   * in our own code, and swallowing it would hide it on every browser.
   */
  set = (
    key: ELocalStorageKeys | ESessionStorageKeys,
    value: unknown,
    forceStorageType?: EStorageType
  ): void => {
    if (!this.isClient) return;
    const storageKey: EStorageType = forceStorageType ?? this.getStorage(key);

    try {
      (window as Window)[storageKey].setItem(key, JSON.stringify(value));
    } catch (error) {
      this.onError?.(key, error);
    }
  };

  get = (
    key: ELocalStorageKeys | ESessionStorageKeys,
    forceStorageType?: EStorageType
  ): any => {
    if (!this.isClient) return null;
    const storageKey: EStorageType = forceStorageType ?? this.getStorage(key);

    try {
      const raw = (window as Window)[storageKey].getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      this.onError?.(key, error);
      return null;
    }
  };

  remove = (key: ELocalStorageKeys | ESessionStorageKeys): void => {
    if (!this.isClient) return;
    const storageKey: EStorageType = this.getStorage(key);

    try {
      (window as Window)[storageKey].removeItem(key);
    } catch (error) {
      this.onError?.(key, error);
    }
  };

  getStorageWithFallback = (): { id: string; ttl: number } | null => {
    const sessionData = this.get(ESessionStorageKeys.SESSION_DATA, EStorageType.SESSION_STORAGE) as
      | { id?: string; ttl?: number }
      | null;

    if (sessionData?.id && sessionData?.ttl) {
      this.set(ELocalStorageKeys.SESSION_DATA, sessionData, EStorageType.LOCAL_STORAGE);
      return sessionData as { id: string; ttl: number };
    }

    const localData = this.get(ELocalStorageKeys.SESSION_DATA, EStorageType.LOCAL_STORAGE) as
      | { id?: string; ttl?: number }
      | null;

    if (localData?.id && localData?.ttl) {
      return localData as { id: string; ttl: number };
    }

    return null;
  };

  abstract clear(): void;
}
